import { useId, useMemo, useState, type ReactNode } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Collapse,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

import {
  getMarginCenterCustomerProductBenchmark,
} from '../services/marginCenterService';

import type {
  MarginCenterCustomerProductBenchmarkMetric,
} from '../types/marginCenter';
import { pricingNumberFormat } from '../../../modules/pricingJourney/formatNumber';


const DISPLAY_CURRENCY = 'EUR';

const numberFormatter = pricingNumberFormat({
    maximumFractionDigits: 2,
  },
);

const currencyFormatter = pricingNumberFormat({
    style: 'currency',
    currency: DISPLAY_CURRENCY,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
);


function formatCurrency(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return currencyFormatter.format(value);
}


function formatPct(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return `${numberFormatter.format(value)} %`;
}


function formatDetailedCurrency(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return pricingNumberFormat({
      style: 'currency',
      currency: DISPLAY_CURRENCY,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    },
  ).format(value);
}


function formatDetailedPct(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return `${pricingNumberFormat({
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    },
  ).format(value)} %`;
}


function formatPercentile(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return `P${Math.round(value)}`;
}


type BenchmarkPerspective = 'product' | 'customer';
type ValueFormatter = (value: number | null | undefined) => string;

function formatDate(value: string | null): string {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
}

function formatDifference(value: number | null, formatValue: ValueFormatter, percentage: boolean): string {
  if (value === null) return '—';
  const amount = Math.abs(value) < 0.00005 ? 0 : value;
  if (percentage) return `${amount > 0 ? '+' : ''}${numberFormatter.format(amount)} p.p.`;
  return `${amount > 0 ? '+' : ''}${formatValue(amount)}`;
}

function relativeRange(metric: MarginCenterCustomerProductBenchmarkMetric): string | null {
  if (!metric.target_evaluable || metric.target_value === null || !metric.distribution) return null;
  if (metric.target_value < metric.distribution.p25) return 'Por debajo de la mitad central';
  if (metric.target_value > metric.distribution.p75) return 'Por encima de la mitad central';
  return 'Dentro de la mitad central';
}

function BenchmarkMetricDetail({
  label,
  metric,
  activePeerRelationships,
  formatValue,
  formatDetailedValue,
  percentage = false,
  populationLabel,
}: {
  label: string;
  metric: MarginCenterCustomerProductBenchmarkMetric;
  activePeerRelationships: number;
  formatValue: ValueFormatter;
  formatDetailedValue: ValueFormatter;
  percentage?: boolean;
  populationLabel: string;
}) {
  const distribution = metric.distribution;
  const position = metric.target_percentile;
  const boundedPosition = position === null ? null : Math.max(0, Math.min(100, position));
  const delta = metric.target_value !== null && metric.reference_aggregate !== null
    ? metric.target_value - metric.reference_aggregate : null;
  const centralRange = relativeRange(metric);
  const costCoverage = metric.coverage;
  const partialCostCoverage = costCoverage !== null && costCoverage !== undefined && (
    costCoverage.peer_cost_coverage_pct < 100 || costCoverage.peer_unresolved_cost_rows > 0
  );

  return (
    <Box sx={{ p: 1.3, borderRadius: 1.7, border: '1px solid rgba(148,163,184,0.14)', bgcolor: 'rgba(15,23,42,0.42)' }}>
      <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 850 }}>{label}</Typography>
      <Box sx={{ mt: 0.8, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Esta relación</Typography>
          <Typography variant="body1" sx={{ color: '#f8fafc', fontWeight: 850, fontVariantNumeric: 'tabular-nums' }}>{formatValue(metric.target_value)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Resto · agregado</Typography>
          <Typography variant="body1" sx={{ color: '#cbd5e1', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{formatValue(metric.reference_aggregate)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Diferencia</Typography>
          <Typography variant="body1" sx={{ color: delta === null || Math.abs(delta) < 0.00005 ? '#94a3b8' : delta > 0 ? '#34d399' : '#fb7185', fontWeight: 850, fontVariantNumeric: 'tabular-nums' }}>
            {formatDifference(delta, formatValue, percentage)}
          </Typography>
        </Box>
      </Box>

      <Typography variant="caption" sx={{ mt: 0.75, color: '#94a3b8', display: 'block' }}>
        {metric.peer_count} de {activePeerRelationships} {populationLabel} con dato disponible.
      </Typography>
      {!metric.target_evaluable && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: '#fbbf24' }}>Sin valor propio calculable en este periodo.</Typography>
      )}
      {metric.peer_count === 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: '#94a3b8' }}>Sin referencia calculable para esta métrica.</Typography>
      )}
      {metric.peer_count === 1 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: '#fbbf24' }}>Referencia basada en una sola relación.</Typography>
      )}
      {distribution && metric.peer_count > 0 && (
        <Box sx={{ mt: 0.9, pt: 0.9, borderTop: '1px solid rgba(148,163,184,0.12)' }}>
          <Typography variant="caption" sx={{ color: '#cbd5e1', display: 'block', fontWeight: 750 }}>
            {centralRange ?? 'Posición propia no calculable'}
          </Typography>
          <Box sx={{ mt: 0.6, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.6 }}>
            <Box><Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>P25</Typography><Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 750 }}>{formatValue(distribution.p25)}</Typography></Box>
            <Box><Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>Mediana</Typography><Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 750 }}>{formatValue(distribution.median)}</Typography></Box>
            <Box><Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>P75</Typography><Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 750 }}>{formatValue(distribution.p75)}</Typography></Box>
          </Box>
          {boundedPosition !== null && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: '#7dd3fc', fontWeight: 850 }}>
                {formatPercentile(position)} · posición frente a {metric.peer_count} {populationLabel}
              </Typography>
              <Box aria-label={`Percentil ${numberFormatter.format(boundedPosition)}; la mitad central ocupa del percentil 25 al 75`} sx={{ height: 10, mt: 0.65, mb: 0.35, borderRadius: 3, bgcolor: 'rgba(148,163,184,0.22)', position: 'relative' }}>
                <Box sx={{ position: 'absolute', left: '25%', width: '50%', height: '100%', borderRadius: 3, bgcolor: 'rgba(56,189,248,0.30)' }} />
                <Box sx={{ position: 'absolute', left: `${boundedPosition}%`, top: -4, width: 3, height: 18, borderRadius: 2, bgcolor: '#7dd3fc', transform: 'translateX(-50%)' }} />
              </Box>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" sx={{ color: '#64748b' }}>P0</Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>P25–P75 · mitad central</Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>P100</Typography>
              </Stack>
            </Box>
          )}
        </Box>
      )}
      {partialCostCoverage && costCoverage && (percentage || label.startsWith('Coste')) && (
        <Typography variant="caption" sx={{ color: '#fbbf24', display: 'block', mt: 0.8 }}>
          Coste resuelto en {numberFormatter.format(costCoverage.peer_cost_coverage_pct)} % de operaciones de referencia.
        </Typography>
      )}
      {distribution && formatValue(distribution.p25) === formatValue(distribution.p75) && distribution.p25 !== distribution.p75 && (
        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.4 }}>
          Rango con mayor precisión: {formatDetailedValue(distribution.p25)} – {formatDetailedValue(distribution.p75)}.
        </Typography>
      )}
    </Box>
  );
}

function BenchmarkCard({
  title,
  selected,
  children,
  onClick,
  detailId,
}: {
  title: string;
  selected: boolean;
  children: ReactNode;
  onClick: () => void;
  detailId: string;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-expanded={selected}
      aria-controls={detailId}
      sx={{ display: 'block', width: '100%', minWidth: 0, p: 1.45, textAlign: 'left', borderRadius: 2, border: selected ? '1px solid rgba(56,189,248,0.56)' : '1px solid rgba(148,163,184,0.17)', bgcolor: selected ? 'rgba(14,165,233,0.075)' : 'rgba(15,23,42,0.28)', '&:hover': { bgcolor: 'rgba(56,189,248,0.07)', borderColor: 'rgba(56,189,248,0.44)' }, '&:focus-visible': { outline: '2px solid #38bdf8', outlineOffset: 2 } }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 850 }}>{title}</Typography>
        {selected ? <KeyboardArrowDownRoundedIcon sx={{ fontSize: 20, color: '#7dd3fc' }} /> : <KeyboardArrowRightRoundedIcon sx={{ fontSize: 20, color: '#94a3b8' }} />}
      </Stack>
      <Box sx={{ mt: 1 }}>{children}</Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.9, color: selected ? '#7dd3fc' : '#94a3b8', fontWeight: 800 }}>
        {selected ? 'Ocultar detalle' : 'Ver detalle'}
      </Typography>
    </ButtonBase>
  );
}

export default function CustomerRelationshipBenchmarkPanel({
  customerId,
  productId,
  startDate,
  endDate,
}: {
  customerId: string;
  productId: string;
  startDate: string | null;
  endDate: string | null;
}) {
  const params = useMemo(() => ({ start_date: startDate ?? undefined, end_date: endDate ?? undefined }), [startDate, endDate]);
  const [selected, setSelected] = useState<BenchmarkPerspective | null>(null);
  const [methodOpen, setMethodOpen] = useState(false);
  const detailId = useId();
  const methodId = useId();
  const benchmarkQuery = useQuery({
    queryKey: ['margin-center', 'customer-product-benchmark', customerId, productId, params],
    queryFn: () => getMarginCenterCustomerProductBenchmark(customerId, productId, params),
    enabled: Boolean(customerId) && Boolean(productId),
  });
  const benchmark = benchmarkQuery.data;
  const productScope = benchmark?.product_across_customers ?? null;
  const customerScope = benchmark?.customer_across_products ?? null;
  const productPeers = productScope?.active_peer_relationships ?? 0;
  const customerPeers = customerScope?.active_peer_relationships ?? 0;
  const customerMargin = customerScope?.metrics.margin_percentage ?? null;

  return (
    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid rgba(148,163,184,0.14)', background: 'linear-gradient(180deg, rgba(15,23,42,0.58) 0%, rgba(8,17,31,0.78) 100%)', boxShadow: '0 10px 28px rgba(2,6,23,0.14)' }}>
      {benchmarkQuery.isFetching && <LinearProgress sx={{ mb: 1 }} />}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'baseline' }} gap={0.5}>
        <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 850 }}>Benchmark comercial</Typography>
        <Typography variant="caption" sx={{ color: '#64748b' }}>Periodo analizado · sin incluir esta relación en las referencias</Typography>
      </Stack>

      {benchmarkQuery.isError && <Alert severity="error" sx={{ mt: 1.25 }}>No se pudo cargar el benchmark comercial.</Alert>}
      {!benchmark && benchmarkQuery.isPending && <Typography variant="body2" sx={{ mt: 1.25, color: '#94a3b8' }}>Cargando referencias comerciales…</Typography>}
      {benchmark?.status === 'empty_dataset' && <Typography variant="body2" sx={{ mt: 1.25, color: '#94a3b8' }}>Sin actividad para comparar en este periodo.</Typography>}

      {(productScope || customerScope) && (
        <>
          <Box sx={{ mt: 1.25, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.2 }}>
            {productScope && (
              <BenchmarkCard title="Mismo producto · Otros clientes" selected={selected === 'product'} onClick={() => { setSelected((was) => was === 'product' ? null : 'product'); setMethodOpen(false); }} detailId={detailId}>
                {productPeers === 0 ? (
                  <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 750 }}>Sin otros compradores activos en este periodo.</Typography>
                ) : (
                  <>
                    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>{productPeers} otros compradores del mismo producto</Typography>
                    <Box sx={{ mt: 0.6, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 0.6 }}>
                      {([
                        ['Precio/ud.', productScope.metrics.average_net_unit_price, formatCurrency],
                        ['Margen %', productScope.metrics.margin_percentage, formatPct],
                        ['Coste/ud.', productScope.metrics.average_unit_cost, formatCurrency],
                      ] as const).map(([label, metric, formatter]) => (
                        <Box key={label} sx={{ minWidth: 0, p: 0.75, bgcolor: 'rgba(15,23,42,0.38)', borderRadius: 1.3 }}>
                          <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8' }}>{label}</Typography>
                          <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 850 }}>{formatter(metric.target_value)}</Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>Otros: {formatter(metric.reference_aggregate)}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </BenchmarkCard>
            )}
            {customerScope && (
              <BenchmarkCard title="Este producto · Dentro de este cliente" selected={selected === 'customer'} onClick={() => { setSelected((was) => was === 'customer' ? null : 'customer'); setMethodOpen(false); }} detailId={detailId}>
                {customerPeers === 0 || !customerMargin ? (
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>Sin otros productos activos para comparar.</Typography>
                ) : (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.7 }}>
                      <Box sx={{ p: 0.8, borderRadius: 1.3, bgcolor: 'rgba(15,23,42,0.38)' }}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Margen de esta relación</Typography>
                        <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 850 }}>{formatPct(customerMargin.target_value)}</Typography>
                      </Box>
                      <Box sx={{ p: 0.8, borderRadius: 1.3, bgcolor: 'rgba(15,23,42,0.38)' }}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Otros productos · agregado</Typography>
                        <Typography variant="h6" sx={{ color: '#cbd5e1', fontWeight: 850 }}>{formatPct(customerMargin.reference_aggregate)}</Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: '#7dd3fc', mt: 0.65, fontWeight: 750 }}>
                      {customerMargin.target_percentile === null ? 'Posición no calculable' : `${formatPercentile(customerMargin.target_percentile)} · entre ${customerMargin.peer_count} otros productos con margen calculable`}
                    </Typography>
                  </>
                )}
              </BenchmarkCard>
            )}
          </Box>
          <Collapse in={selected !== null} unmountOnExit>
            <Box id={detailId} sx={{ mt: 1.1, p: { xs: 1.25, md: 1.7 }, borderRadius: 2, border: '1px solid rgba(56,189,248,0.30)', bgcolor: 'rgba(12,28,46,0.76)' }} role="region" aria-label="Detalle del benchmark comercial">
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <Box>
                  <Typography variant="overline" sx={{ color: '#7dd3fc', fontWeight: 850 }}>Detalle del benchmark</Typography>
                  <Typography variant="subtitle1" sx={{ color: '#f8fafc', fontWeight: 850 }}>
                    {selected === 'product' ? 'Este producto frente a otros compradores' : 'Este producto frente al resto del cliente'}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 0.25 }}>
                    {formatDate(benchmark?.selection.start_date ?? null)} – {formatDate(benchmark?.selection.end_date ?? null)}
                  </Typography>
                </Box>
                <Button variant="outlined" size="small" onClick={() => setSelected(null)} sx={{ flexShrink: 0, color: '#94a3b8', borderColor: 'rgba(148,163,184,0.30)', textTransform: 'none' }}>Cerrar</Button>
              </Stack>
              {selected === 'product' && productScope && (
                productPeers === 0 ? (
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1.1 }}>Solo se comparan compradores del mismo artículo. No se utilizan otros productos ni artículos de su categoría como referencia.</Typography>
                ) : (
                  <Stack spacing={1} sx={{ mt: 1.1 }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>{productPeers} otros compradores activos · misma referencia de producto</Typography>
                    <BenchmarkMetricDetail label="Precio neto medio por unidad" metric={productScope.metrics.average_net_unit_price} activePeerRelationships={productPeers} formatValue={formatCurrency} formatDetailedValue={formatDetailedCurrency} populationLabel="otros compradores" />
                    <BenchmarkMetricDetail label="Margen porcentual" metric={productScope.metrics.margin_percentage} activePeerRelationships={productPeers} formatValue={formatPct} formatDetailedValue={formatDetailedPct} percentage populationLabel="otros compradores" />
                    <BenchmarkMetricDetail label="Coste medio por unidad" metric={productScope.metrics.average_unit_cost} activePeerRelationships={productPeers} formatValue={formatCurrency} formatDetailedValue={formatDetailedCurrency} populationLabel="otros compradores" />
                  </Stack>
                )
              )}
              {selected === 'customer' && customerScope && (
                customerPeers === 0 ? (
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1.1 }}>Sin otros productos activos del cliente en este periodo.</Typography>
                ) : (
                  <Stack spacing={1} sx={{ mt: 1.1 }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>{customerPeers} otros productos activos · mismo cliente · solo margen comparable</Typography>
                    <BenchmarkMetricDetail label="Margen porcentual" metric={customerScope.metrics.margin_percentage} activePeerRelationships={customerPeers} formatValue={formatPct} formatDetailedValue={formatDetailedPct} percentage populationLabel="otros productos" />
                  </Stack>
                )
              )}
              <Button size="small" variant="text" onClick={() => setMethodOpen((v) => !v)} aria-expanded={methodOpen} aria-controls={methodId} sx={{ mt: 1, textTransform: 'none', color: '#7dd3fc' }}>
                {methodOpen ? 'Ocultar método' : '¿Cómo se calcula?'}
              </Button>
              <Collapse in={methodOpen} unmountOnExit>
                <Box id={methodId} sx={{ p: 1, borderRadius: 1.5, bgcolor: 'rgba(15,23,42,0.45)' }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', lineHeight: 1.55 }}>
                    Se compara la misma selección temporal. Esta relación no forma parte de su referencia. El agregado se calcula con sumas financieras, no con la media de porcentajes por relación. P25, mediana y P75 describen la distribución de valores individuales. El percentil usa las referencias por debajo y la mitad de los empates; no es un puesto ni porcentaje de ingresos. Los costes no resueltos no se consideran cero. Esta posición aporta contexto, no una recomendación de precio.
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          </Collapse>
          {benchmark?.status === 'candidate_relationship' && (
            <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 1 }}>
              Relación sin actividad propia en el periodo: la referencia puede existir, pero su posición no es calculable.
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
}
